import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { makeAppError } from './errors'

/**
 * Null-safe Supabase browser client (contract §7.1).
 * Only browser-safe values are read here. A service-role key must NEVER
 * appear in this module or any VITE_* variable.
 */
const url = import.meta.env.VITE_SUPABASE_URL as string | undefined
const anonKey = (
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ??
  import.meta.env.VITE_SUPABASE_ANON_KEY
) as string | undefined

let client: SupabaseClient | null = null

if (url && anonKey) {
  client = createClient(url, anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
  })
}

/** Returns the client or null when env config is absent. */
export function getSupabase(): SupabaseClient | null {
  return client
}

/** Returns the client or throws a typed, user-safe AppError. */
export function requireSupabase(): SupabaseClient {
  if (!client) {
    throw makeAppError({
      code: 'SUPABASE_NOT_CONFIGURED',
      userMessage:
        'De cloudverbinding is niet ingesteld. Je gegevens blijven op dit apparaat bewaard.',
      technicalMessage: 'VITE_SUPABASE_URL or a Supabase publishable/anon key is missing',
      retryable: false,
      source: 'database',
    })
  }
  return client
}

export function isCloudConfigured(): boolean {
  return client !== null
}

/** Private storage buckets (contract §10.7). */
export const BUCKETS = {
  placeImages: 'place-images',
  medicationProofVideos: 'medication-proof-videos',
  avatars: 'avatars',
} as const

/** Signed URL lifetime for private objects, in seconds. Short-lived by design. */
export const SIGNED_URL_TTL_SECONDS = 60 * 10
